import React, {useEffect, useRef, useState} from "react";
import {Table, Button, Typography} from "antd";
import {useTranslation} from "react-i18next";

import api from "../api";
import AssetBrowseImage from "../explore/AssetBrowseImage";
import useShowAsset from "../helpers/useShowAsset";
import PaginationTotal from "../widgets/PaginationTotal";

// Paginated assets table reused by job-style records that have an
// /assets sub-resource. Pass apiPath (e.g. "/api/bulk_jobs/<guid>/assets" or
// "/api/meta_imports/123/assets"), and assetField if the row's asset is nested
// under a key (meta-import asset rows are MetaImportAsset wrappers with an
// `.asset` field; bulk-job assets are returned as Asset directly).
//
// extraColumns is appended after the built-in columns.
export default ({apiPath, assetField, extraColumns = [], pageSize = 25, refreshKey}) => {
    const {t} = useTranslation();
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({current: 1, pageSize});
    const [loading, setLoading] = useState(false);

    const showAsset = useShowAsset();

    // Pull the asset object out of the row (direct or nested)
    const assetOf = row => assetField ? row[assetField] : row;

    const click = row => {
        const asset = assetOf(row);
        if (asset?.id) showAsset(asset.id);
    };

    const baseColumns = [
        {
            title: t('thumb', 'Thumb'),
            key: 'thumb',
            render: row => (
                <a onClick={() => click(row)}>
                    <AssetBrowseImage asset={assetOf(row)} assetSize={40} thumbType={'thumb'} padding={'0.5em'}/>
                </a>
            )
        },
        {
            title: t('filename', 'Filename'),
            key: 'filename',
            sorter: true,
            render: row => {
                const asset = assetOf(row);
                return <Button type={'link'} onClick={() => click(row)}>{asset?.filename}</Button>;
            }
        },
        {
            title: 'GUID',
            key: 'guid',
            render: row => {
                const guid = assetOf(row)?.guid || '';
                if (!guid) return null;
                return <Typography.Text copyable={{text: guid}} type={'code'}>{guid.slice(0, 8)}</Typography.Text>;
            }
        },
    ];

    const columns = [...baseColumns, ...extraColumns];

    const params = useRef({});

    const handleTableChange = (pagination, filters, sorter) => {
        params.current = {
            sortField: sorter?.field,
            sortOrder: sorter?.order,
            pagination,
            ...filters,
        };
        loadTableData();
    };

    const loadTableData = () => {
        setLoading(true);
        api.get(apiPath, {params: params.current}).then(res => {
            setData(res.data);
            setPagination({
                ...params.current.pagination,
                total: res.headers['total-entries'],
                pageSize,
            });
            setLoading(false);
        });
    };

    useEffect(() => {
        loadTableData();
    }, [apiPath, refreshKey]);

    return (
        <Table
            bordered
            scroll={{x: true}}
            size={'small'}
            columns={columns}
            rowKey={record => record.id || record.guid || `${record.asset_id}-${record.filename}`}
            dataSource={data}
            pagination={pagination}
            loading={loading}
            onChange={handleTableChange}
            footer={() => (
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <PaginationTotal pagination={pagination}/>
                </div>
            )}
        />
    );
};
